import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { X, MessageSquare, Users, AlertCircle } from 'lucide-react';
import { apiService } from '../../services/api';

interface TicketMassMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMessageSent: () => void;
}

const TicketMassMessageModal: React.FC<TicketMassMessageModalProps> = ({
  isOpen,
  onClose,
  onMessageSent
}) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    sendOnlyToOnline: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{
    totalUsers: number;
    successCount: number;
    errorCount: number;
  } | null>(null);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.content.trim()) {
      return;
    }

    try {
      setIsLoading(true);
      setResult(null);
      
      const data = await apiService.sendMassMessage(formData);
      setResult(data.data);
      onMessageSent();
    } catch (error) {
      console.error('Erro ao enviar mensagem em massa:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      content: '',
      sendOnlyToOnline: false
    });
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              <span>Mensagem em Massa</span>
            </div>
            <Button variant="outline" size="sm" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Aviso */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">
                    Atenção: Mensagem em Massa
                  </h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    Esta mensagem será enviada para todos os usuários ativos do sistema como um novo ticket.
                    Use com responsabilidade e apenas para comunicações importantes.
                  </p>
                </div>
              </div>
            </div>

            {/* Título */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Título do Ticket
              </label>
              <Input
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Digite o título do ticket (opcional)"
              />
              <p className="text-xs text-gray-500">
                Se não preenchido, será usado "Mensagem do Administrador"
              </p>
            </div>

            {/* Conteúdo */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Conteúdo da Mensagem <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={formData.content}
                onChange={(e) => handleInputChange('content', e.target.value)}
                placeholder="Digite a mensagem que será enviada para todos os usuários..."
                rows={8}
                required
              />
              <p className="text-xs text-gray-500">
                Esta mensagem será enviada como um novo ticket para cada usuário ativo.
              </p>
            </div>

            {/* Opção de envio */}
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.sendOnlyToOnline}
                  onChange={(e) => setFormData({ ...formData, sendOnlyToOnline: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enviar apenas para usuários online
                </span>
              </label>
              <p className="text-xs text-gray-500">
                {formData.sendOnlyToOnline 
                  ? "A mensagem será enviada apenas para usuários que estão atualmente logados no painel"
                  : "A mensagem será enviada para todos os usuários ativos (incluindo os que não estão online)"
                }
              </p>
            </div>

            {/* Botões */}
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!formData.content.trim() || isLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {isLoading ? 'Enviando...' : 'Enviar para Todos'}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            {/* Resultado */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <MessageSquare className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-green-800">
                    Mensagem Enviada com Sucesso!
                  </h3>
                  <p className="text-sm text-green-700 mt-1">
                    A mensagem foi processada e enviada para os usuários.
                  </p>
                </div>
              </div>
            </div>

            {/* Estatísticas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Total de Usuários</span>
                </div>
                <p className="text-2xl font-bold text-blue-900 mt-1">
                  {result.totalUsers}
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-green-800">Enviados com Sucesso</span>
                </div>
                <p className="text-2xl font-bold text-green-900 mt-1">
                  {result.successCount}
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <span className="text-sm font-medium text-red-800">Erros</span>
                </div>
                <p className="text-2xl font-bold text-red-900 mt-1">
                  {result.errorCount}
                </p>
              </div>
            </div>

            {/* Botão de Fechar */}
            <div className="flex justify-end">
              <Button onClick={handleClose}>
                Fechar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TicketMassMessageModal;
